import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import {
  type HeySynclyAnswerStudioItem,
  type HeySynclyChat,
  type HeySynclyChatMode,
  HeySynclyMessageDisplay,
} from '@/entities/hey-syncly';
import { cn } from '@/lib/cn';
import useHeySynclyStore from '@/stores/HeySynclyStore';

import { useHeySynclyPageStore } from '../../model/hey-syncly-page-store';
import ChatInputContainer from './chat-input/chat-input-container';

const ChatPanel = ({
  chat,
  chatMode,
  onChatModeChange,
}: {
  chat: HeySynclyChat | null;
  chatMode: HeySynclyChatMode;
  onChatModeChange: (nextMode: HeySynclyChatMode) => void;
}) => {
  const answer = useHeySynclyStore((state) => state.tempAnswer);
  const setStudioItemDetail = useHeySynclyStore(
    (state) => state.setStudioItemDetail,
  );
  const [showScrollToBottomButton, setShowScrollToBottomButton] =
    useState(false);
  const debouncedSetShowScrollToBottomButton = useDebouncedCallback(
    (nextVisible: boolean) => {
      setShowScrollToBottomButton(nextVisible);
    },
    150,
  );

  const onStudioItemClick = (item: HeySynclyAnswerStudioItem) => {
    setStudioItemDetail(item.id);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const currentChatAlias = useHeySynclyPageStore(
    (state) => state.currentChatAlias,
  );
  const hasActiveChat = currentChatAlias !== null;

  const getScrollViewport = useCallback(() => {
    return containerRef.current?.querySelector<HTMLDivElement>(
      '[data-slot="scroll-area-viewport"]',
    );
  }, []);

  const updateScrollButtonVisibility = useCallback(() => {
    const viewport = getScrollViewport();
    if (!viewport) return;

    const threshold = 24;
    const hasOverflow = viewport.scrollHeight > viewport.clientHeight + threshold;
    const isAtBottom =
      viewport.scrollTop + viewport.clientHeight >=
      viewport.scrollHeight - threshold;
    const nextVisible = hasOverflow && !isAtBottom;

    if (!nextVisible) {
      debouncedSetShowScrollToBottomButton.cancel();
      setShowScrollToBottomButton(false);
      return;
    }

    debouncedSetShowScrollToBottomButton(true);
  }, [debouncedSetShowScrollToBottomButton, getScrollViewport]);

  const scrollToBottom = useCallback(() => {
    const viewport = getScrollViewport();
    if (!viewport) return;

    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: 'smooth',
    });
  }, [getScrollViewport]);

  useEffect(() => {
    if (!chat) {
      debouncedSetShowScrollToBottomButton.cancel();
      setShowScrollToBottomButton(false);
    }
  }, [chat, debouncedSetShowScrollToBottomButton]);

  useEffect(() => {
    return () => {
      debouncedSetShowScrollToBottomButton.cancel();
    };
  }, [debouncedSetShowScrollToBottomButton]);

  useEffect(() => {
    updateScrollButtonVisibility();

    const viewport = getScrollViewport();
    if (!viewport) {
      return;
    }

    const handleScroll = () => {
      updateScrollButtonVisibility();
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && contentRef.current
        ? new ResizeObserver(() => {
            updateScrollButtonVisibility();
          })
        : null;

    if (resizeObserver && contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
    };
  }, [
    chat?.alias,
    chat?.messages.length,
    answer?.answer,
    answer?.reason,
    answer?.status,
    getScrollViewport,
    updateScrollButtonVisibility,
  ]);

  return (
    <div
      className={cn(
        'relative flex size-full flex-1 flex-col items-center',
        hasActiveChat ? 'overflow-hidden' : 'overflow-y-auto',
      )}
      style={{ scrollbarGutter: 'stable' }}
    >
      {chat && (
        <div className="relative min-w-0 size-full">
          <ScrollArea
            // ScrollArea 내부 display table때문에 반응형 width 대응
            className="h-[calc(100vh-72px)] w-full min-w-0 flex-1 px-6 [&>[data-slot=scroll-area-viewport]]:min-w-0 [&>[data-slot=scroll-area-viewport]]:[overflow-anchor:none] [&>[data-slot=scroll-area-viewport]>div]:!block [&>[data-slot=scroll-area-viewport]>div]:!w-full [&>[data-slot=scroll-area-viewport]>div]:!min-w-0"
            ref={containerRef}
          >
            <div
              ref={contentRef}
              className="w-full min-w-0 [overflow-anchor:none]"
            >
              <HeySynclyMessageDisplay
                chat_id={chat.alias}
                messages={chat.messages}
                status={chat.status}
                answer={answer}
                onStudioItemClick={onStudioItemClick}
                container={containerRef}
              />
            </div>
          </ScrollArea>
        </div>
      )}

      <ChatInputContainer
        chat={chat}
        chatMode={chatMode}
        onScrollToBottom={scrollToBottom}
        onChatModeChange={onChatModeChange}
        showScrollToBottomButton={showScrollToBottomButton}
      />
    </div>
  );
};

export default ChatPanel;
